import { useState, useCallback } from 'react';
import { togglePhotoLike } from '../../utils/api/api';

interface UsePhotoLikeReturn {
  handleLike: (photoId: string, currentLikes: number, currentIsLiked: boolean) => Promise<{
    success: boolean;
    newLikes: number;
    newIsLiked: boolean;
  }>;
  isProcessing: boolean;
  error: string | null;
}

/**
 * いいね処理用カスタムフック
 * - 楽観的更新（Optimistic Update）
 * - エラーハンドリング
 * - 処理中状態の管理
 * - 重複実行の防止
 */
export const usePhotoLike = (): UsePhotoLikeReturn => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLike = useCallback(async (
    photoId: string, 
    currentLikes: number, 
    currentIsLiked: boolean
  ) => {
    // 重複実行防止
    if (isProcessing) {
      return {
        success: false,
        newLikes: currentLikes,
        newIsLiked: currentIsLiked
      };
    }

    setIsProcessing(true);
    setError(null);

    // 楽観的更新の値を計算
    const optimisticIsLiked = !currentIsLiked;

    try {
      const response = await togglePhotoLike(photoId);

      if (response.success) {
        return {
          success: true,
          newLikes: response.likesCount,
          newIsLiked: response.isLikedByCurrentUser
        };
      } else {
        throw new Error(response.message || 'いいねの更新に失敗しました');
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'いいねの更新に失敗しました';
      setError(errorMessage);
      
      console.error('❌ いいねの更新に失敗しました:', err);
      
      // エラー時は楽観的更新を取り消し（元の値を返す）
      return {
        success: false,
        newLikes: currentLikes,
        newIsLiked: currentIsLiked
      };
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing]);

  return {
    handleLike,
    isProcessing,
    error
  };
};

export default usePhotoLike;